import { supabase } from '@common/api/authService';
import { useUserStorage } from '@common/hooks/useUserStorage';
import {
    useCallback, useEffect, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

export type WorkspaceRole = 'OWNER' | 'ADMIN' | 'MEMBER';
export type MemberStatus = 'ACTIVE' | 'INVITED' | 'DECLINED' | 'LEFT';

export interface WorkspaceSubscription {
    id: string;
    plan_id: string;
    status: string;
    current_period_end: string | null;
    trial_end_at: string | null;
    cancel_at_period_end: boolean;
    // Plan info from benefit_plan
    plan_code: string;
    plan_name: string;
    plan_tier: string;
    billing_cycle: string;
}

export interface Workspace {
    id: string;
    name: string;
    logo_url?: string;
    owner_id: string;
    settings?: Record<string, any>;
    status: 'ACTIVE' | 'INACTIVE'; // 根据 user input 推断，或者保持 string
    created_at: string;
    updated_at: string;
    role?: WorkspaceRole; // 附加角色信息
    subscription?: WorkspaceSubscription | null; // 附加订阅信息
}

export interface UseWorkspacesReturn {
    workspaces: Workspace[];
    currentWorkspace: Workspace | null;
    isLoading: boolean;
    error: Error | null;
    refreshWorkspaces: (options?: { silent?: boolean }) => Promise<void>;
    switchWorkspace: (workspaceId: string, params?: Record<string, string>) => void;
}

export const useWorkspaces = (userId?: string): UseWorkspacesReturn => {
    const { get, set } = useUserStorage();
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(() => {
        const workspaceId = get('currentWorkspaceId');
        window.Bustly = {workspace_id: workspaceId};
        return workspaceId || null;
    });
    const navigate = useNavigate();
    console.log("currentWorkspaceId", currentWorkspaceId)
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const currentWorkspace = workspaces.find(w => w.id === currentWorkspaceId) || null;

    const _setCurrentWorkspaceId = useCallback((workspaceId: string | null) => {
        console.log("Setting current workspace ID:", workspaceId);
        window.Bustly = {workspace_id: workspaceId};
        set('currentWorkspaceId', workspaceId);
        setCurrentWorkspaceId(workspaceId);
    }, [set]);

    const fetchWorkspaces = useCallback(async (options?: { silent?: boolean }) => {
        if (!userId) {
            setWorkspaces([]);
            return;
        }

        if (!options?.silent) {
            setIsLoading(true);
        }
        setError(null);

        try {
            // 直接查询 workspaces 表，并关联查询 workspace_members 获取当前用户的角色
            // 以及 workspace_subscriptions 和 benefit_plan 获取订阅信息
            // @ts-ignore
            const { data, error } = await supabase
                .from('workspaces')
                .select(`
                    *,
                    workspace_members (
                        role,
                        user_id
                    ),
                    workspace_subscriptions (
                        id,
                        plan_id,
                        status,
                        current_period_end,
                        trial_end_at,
                        cancel_at_period_end,
                        benefit_plan (
                            code,
                            name,
                            tier,
                            billing_cycle
                        )
                    )
                `)
                .eq('status', 'ACTIVE')
                .eq('workspace_members.user_id', userId)
                .order('created_at', { ascending: false });

            if (error) throw error;

            // 转换数据结构，将 role 和 subscription 提升到 Workspace 对象顶层
            const formattedWorkspaces = data.map((item: any) => {
                // 因为关联查询加了 user_id 过滤，所以 workspace_members 数组里应该只有当前用户的记录
                const role = item.workspace_members?.[0]?.role || 'MEMBER';

                // 获取有效的订阅（未过期或试用期未结束）
                const subscription = item.workspace_subscriptions && item.workspace_subscriptions.length > 0
                    ? {
                        id: item.workspace_subscriptions[0].id,
                        plan_id: item.workspace_subscriptions[0].plan_id,
                        status: item.workspace_subscriptions[0].status,
                        current_period_end: item.workspace_subscriptions[0].current_period_end,
                        trial_end_at: item.workspace_subscriptions[0].trial_end_at,
                        cancel_at_period_end: item.workspace_subscriptions[0].cancel_at_period_end,
                        plan_code: item.workspace_subscriptions[0].benefit_plan?.code || 'basic',
                        plan_name: item.workspace_subscriptions[0].benefit_plan?.name || 'Basic',
                        plan_tier: item.workspace_subscriptions[0].benefit_plan?.tier || 'free',
                        billing_cycle: item.workspace_subscriptions[0].benefit_plan?.billing_cycle || 'monthly',
                    }
                    : null;

                // 移除嵌套数据，避免混淆
                const { workspace_members, workspace_subscriptions, ...workspace } = item;
                return {
                    ...workspace,
                    role,
                    subscription
                };
            });

            setWorkspaces(formattedWorkspaces);

            // 当 workspace_id 为空时，自动设置第一个为当前 workspace
            if (formattedWorkspaces.length > 0 && !currentWorkspaceId) {
                const firstWorkspaceId = formattedWorkspaces[0].id;
                console.log('自动设置第一个 workspace 为当前 workspace:', firstWorkspaceId);
                _setCurrentWorkspaceId(firstWorkspaceId);
            }
            if(formattedWorkspaces.length === 0){
                navigate('/onboarding');
            }
        } catch (err: any) {
            console.error('Error fetching workspaces:', err);
            setError(err);
        } finally {
            if (!options?.silent) {
                setIsLoading(false);
            }
        }
    }, [userId, currentWorkspaceId, _setCurrentWorkspaceId, navigate]);

    useEffect(() => {
        if (!userId) return;
        fetchWorkspaces();
    }, [userId, fetchWorkspaces]);

    const switchWorkspace = useCallback((workspaceId: string, params?: Record<string, string>) => {
        console.log("Switching to workspace:", workspaceId, params);
        _setCurrentWorkspaceId(workspaceId);

        // Build URL with optional query parameters
        const url = new URL('/admin', window.location.origin);
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                url.searchParams.set(key, value);
            });
        }

        window.location.href = url.toString();
    }, [_setCurrentWorkspaceId]);

    return {
        workspaces,
        currentWorkspace,
        isLoading,
        error,
        refreshWorkspaces: fetchWorkspaces,
        switchWorkspace,
    };
};
